import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Archive, Eye, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { auth, db } from "../firebase";
import type { ArchiveSummary } from "./archiveCycleModal";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { MELT_BUTTON_QUIET, MELT_DIALOG } from "./ui/melt";
import { EDIT_INPUT, MENU_ITEM } from "./applicationShared";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { cn } from "../lib/utils";

// Small borderless action button in an archive row.
const ROW_ACTION =
  "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-melt-text2 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1 disabled:opacity-50";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

type ArchiveRow = {
  id: string;
  name: string;
  count: number;
  status: string;
  summary: ArchiveSummary | null;
};

// "2026-01-10" -> "Jan 2026"
function formatMonth(iso?: string | null) {
  if (!iso) return null;
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function describe(a: ArchiveRow) {
  const parts: string[] = [];
  const first = formatMonth(a.summary?.firstApplicationDate);
  const last = formatMonth(a.summary?.lastApplicationDate);
  if (first && last) parts.push(first === last ? first : `${first} – ${last}`);
  parts.push(`${a.count} application${a.count === 1 ? "" : "s"}`);
  if (a.summary) {
    parts.push(`${a.summary.reached.interview} interview${a.summary.reached.interview === 1 ? "" : "s"}`);
    parts.push(`${a.summary.reached.offer} offer${a.summary.reached.offer === 1 ? "" : "s"}`);
  }
  return parts.join(" · ");
}

async function authedFetch(path: string, init: RequestInit) {
  const user = auth.currentUser;
  if (!user) throw new Error("Please log in again to continue.");
  const r = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${await user.getIdToken()}`,
      "Content-Type": "application/json",
    },
  });
  const json = await r.json().catch(() => ({}));
  if (json?.error === "INVALID_NAME") throw new Error("Give the archive a name (80 characters max).");
  if (json?.error === "NOT_FOUND") throw new Error("That archive doesn't exist anymore.");
  if (!r.ok) throw new Error(json?.error || "Something went wrong");
  return json;
}

// Past recruiting cycles in Settings → Archives: view, rename (inline), delete.
export default function ArchiveList({ uid }: { uid: string }) {
  const navigate = useNavigate();
  const [archives, setArchives] = useState<ArchiveRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ArchiveRow | null>(null);

  useEffect(() => {
    const q = query(collection(db, "users", uid, "archives"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setArchives(
          snapshot.docs.map((d) => {
            const a = d.data();
            return {
              id: d.id,
              name: a.name || "Untitled archive",
              count: a.count ?? 0,
              status: a.status || "complete",
              summary: a.summary ?? null,
            };
          })
        );
        setLoading(false);
      },
      (e) => {
        setError(e.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [uid]);

  const startRename = (a: ArchiveRow) => {
    setRenamingId(a.id);
    setRenameValue(a.name);
    setError("");
  };

  const saveRename = async (id: string) => {
    setBusyId(id);
    setError("");
    try {
      await authedFetch(`/archives/${id}`, { method: "PATCH", body: JSON.stringify({ name: renameValue.trim() }) });
      setRenamingId(null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to rename");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    setError("");
    try {
      await authedFetch(`/archives/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p className="font-plex text-xs text-melt-text3">Loading archives…</p>;

  return (
    <div className="space-y-3">
      {archives.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-melt-text1/[.16] px-5 py-6 text-center text-sm text-melt-text3">
          No archived cycles yet. When you archive a cycle, it shows up here.
        </p>
      ) : (
        <ul className="divide-y divide-melt-hair rounded-2xl border border-melt-hair bg-white">
          {archives.map((a) => {
            const busy = busyId === a.id;
            const archiving = a.status !== "complete";
            return (
              <li key={a.id} className="flex items-center gap-4 px-5 py-4">
                <span className="grid h-10 w-10 flex-none place-items-center rounded-[10px] bg-[#F2EDE4] text-melt-text2">
                  <Archive size={18} strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  {renamingId === a.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveRename(a.id);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        maxLength={80}
                        autoFocus
                        className={cn(EDIT_INPUT, "text-sm")}
                      />
                      <button
                        onClick={() => saveRename(a.id)}
                        disabled={busy || !renameValue.trim()}
                        className="rounded-lg bg-melt-caramel px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-melt-press disabled:opacity-45"
                      >
                        {busy ? "Saving…" : "Save"}
                      </button>
                      <button
                        onClick={() => setRenamingId(null)}
                        disabled={busy}
                        className={ROW_ACTION}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="truncate text-[15px] font-semibold text-melt-text1">{a.name}</p>
                      <p className="mt-0.5 font-plex text-xs text-melt-text3">
                        {archiving ? "Archiving…" : describe(a)}
                      </p>
                    </>
                  )}
                </div>

                {renamingId !== a.id && (
                  <div className="flex flex-none items-center gap-1">
                    <button
                      onClick={() => navigate(`/settings/archives/${a.id}`)}
                      disabled={archiving}
                      className={ROW_ACTION}
                    >
                      <Eye size={15} strokeWidth={1.75} />
                      View
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          aria-label={`Options for ${a.name}`}
                          className="grid h-8 w-8 place-items-center rounded-lg text-melt-text3 outline-none transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1 focus-visible:ring-2 focus-visible:ring-melt-caramel/30 data-[state=open]:bg-melt-text1/[.06]"
                        >
                          <MoreHorizontal size={17} strokeWidth={1.75} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        // Rename swaps in an autofocused input; don't pull focus back to the trigger
                        onCloseAutoFocus={(e) => e.preventDefault()}
                        className="w-40 rounded-[11px] border-melt-hair bg-melt-menu p-[5px] font-grotesk shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
                      >
                        <DropdownMenuItem onSelect={() => startRename(a)} className={MENU_ITEM}>
                          <Pencil size={15} strokeWidth={1.75} />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-melt-hair" />
                        <DropdownMenuItem
                          onSelect={() => setDeleteTarget(a)}
                          className={cn(MENU_ITEM, "text-[#A5492E] focus:bg-[#A5492E]/[.07] [&_svg]:text-[#A5492E]")}
                        >
                          <Trash2 size={15} strokeWidth={1.75} />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-[10px] bg-[#F7E6DE] px-4 py-3 text-sm text-[#9A4F33]">
          <AlertCircle size={16} strokeWidth={1.75} />
          {error}
        </div>
      )}

      {deleteTarget && (
        <Dialog open onOpenChange={(open) => !open && !busyId && setDeleteTarget(null)}>
          <DialogContent showClose={false} className={cn(MELT_DIALOG, "max-w-[400px] px-7 pb-6 pt-7")}>
            <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">
              Delete “{deleteTarget.name}”?
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-melt-text2">
              This permanently deletes the archive and its {deleteTarget.count} application
              {deleteTarget.count === 1 ? "" : "s"}. This can't be undone.
            </DialogDescription>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={!!busyId}
                className={MELT_BUTTON_QUIET}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!!busyId}
                className="inline-flex h-11 items-center rounded-xl bg-[#A5492E] px-5 text-[15px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {busyId ? "Deleting…" : "Delete archive"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
